import { useEffect, useState } from 'react';

/**
 * @file THE DEATH SPLASH: a red vignette and the word, centre screen, for the
 * three seconds between dying and waking up at the plaza.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT — TWO CHANNELS, AND NO CLOCK
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * bus.emit('player:wasted',  {x, z})   // raise it — where the player fell
 * bus.emit('player:respawn', {x, z})   // drop it  — where they came back
 * ```
 * The pair is deliberate, and the alternative was rejected on purpose:
 * `'wanted:level'` reaching 0 ALSO happens on a respawn, but it happens on
 * every ordinary cool-down too, so a splash keyed to it would be torn down by
 * the heat expiring while the player was still lying in the road. `systems/
 * Gameplay.js` owns the beat (`FIRE.respawnDelay`) and this component owns none
 * of it — there is no `setTimeout` here, so the splash cannot drift out of step
 * with the sim, and pausing the engine holds it up rather than ending it early.
 *
 * ⚠ The two channels are subscribed DIRECTLY rather than through
 * `useEngineBus`: that hook throttles and keeps the last payload, which is
 * exactly wrong for an edge — a respawn arriving inside the throttle window
 * would be swallowed and the splash would stick.
 *
 * The whole thing is `pointer-events-none` and `aria-live`, so it never eats a
 * click and a screen reader announces it once.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */
export default function Wasted({ engine }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const bus = engine?.bus;
    if (!bus) return undefined;
    const offs = [
      bus.on('player:wasted', () => setShown(true)),
      bus.on('player:respawn', () => setShown(false)),
      // A level teardown leaves nothing to respawn INTO; the splash must not
      // survive it and sit over the next level's loading screen.
      bus.on('level:loading', () => setShown(false)),
    ];
    return () => {
      for (const off of offs) off?.();
      setShown(false);
    };
  }, [engine]);

  if (!shown) return null;

  return (
    <div
      className="wasted-splash pointer-events-none absolute inset-0 z-40 flex items-center justify-center"
      role="status"
      aria-live="assertive"
    >
      {/* The tint is a RADIAL gradient, not a flat wash: a full-screen red
          rectangle hides the world, and the point of the shot is that you can
          still see what killed you standing over you. */}
      <div className="wasted-vignette absolute inset-0" />
      <span className="wasted-word gta-title font-display text-[clamp(3rem,13vw,9rem)] leading-none tracking-[0.04em]">
        WASTED
      </span>
    </div>
  );
}
